// Prefetches the publisher experience JSON for anchors as they near the
// viewport, sharing one in-flight registry with the mapping scripts.
import urlUtils from "@/common/utils/urlUtils";
import ExperienceAttributes from "@/modules/ExperienceAttributes";
import { warmDefaultPlayIcons } from "@/modules/assetImageCache";
import {
  buildPublisherExperienceApiUrl,
  buildPublisherExperienceByUrlApiUrl,
} from "@/modules/experienceApiUrl";
import { normalizeExperienceBody } from "@/modules/experiencePayloadUtils";
import { injectPlayShellPreloadHints } from "@/modules/experiencePrefetchWarm";
import type { PrefetchEntry } from "@/types/globals";
import type { InflightRegistry, PrefetchBucket } from "@/popover/types";

/**
 * Per-cacheKey fetch dedupe for embed prefetch. Stored on `window.__pixmoto`
 * so the vanilla mapping shim (`pixmoto_video_mapping_common.js`) and
 * `popover.js` share the same inflight registry and don't double-fetch the
 * publisher experience API when both code paths run on the same page.
 */
function _getInflightRegistry(): InflightRegistry {
  const pm = window.__pixmoto || {};
  window.__pixmoto = pm;
  if (!pm._prefetchInflight) {
    pm._prefetchInflight = {};
  }
  return pm._prefetchInflight;
}

export function experienceIdFromPublisherPayload(json: unknown): string | null {
  if (!json || typeof json !== "object") {
    return null;
  }
  try {
    const norm = normalizeExperienceBody(json);
    if (norm && norm.idexperience != null) {
      return String(norm.idexperience);
    }
  } catch (_e) {
    /* ignore */
  }
  if ((json as { idexperience?: unknown }).idexperience != null) {
    return String((json as { idexperience?: unknown }).idexperience);
  }
  return null;
}

function _ensurePrefetchedExperiencesBucket(): PrefetchBucket {
  const pm = window.__pixmoto || {};
  window.__pixmoto = pm;
  if (!pm.prefetchedExperiences) {
    pm.prefetchedExperiences = {};
  }
  return pm.prefetchedExperiences;
}

function _prefetchInflightAny(
  inflight: InflightRegistry,
  keys: string[],
): boolean {
  for (let i = 0; i < keys.length; i++) {
    if (keys[i] && inflight[keys[i]]) {
      return true;
    }
  }
  return false;
}

/** Reuse mapping prefetch (`url:` key) when anchor already has `data-pix-expid`. */
function _reuseMappedPrefetchEntry(
  bucket: PrefetchBucket,
  expId: string | true | undefined,
  mapPageUrl: string | true | null,
): { key: string; entry: PrefetchEntry } | null {
  if (expId) {
    const idKey = String(expId);
    const byId = bucket[idKey];
    if (byId && byId.status === "ok") {
      return { key: idKey, entry: byId };
    }
  }
  if (mapPageUrl) {
    const urlKey = `url:${mapPageUrl}`;
    const byUrl = bucket[urlKey];
    if (byUrl && byUrl.status === "ok") {
      if (expId) {
        bucket[String(expId)] = byUrl;
      }
      return {
        key: expId ? String(expId) : urlKey,
        entry: byUrl,
      };
    }
  }
  return null;
}

export function runPrefetchForAnchor(anchor: Element) {
  runPrefetch(
    ExperienceAttributes.fromCombinedAttributes(
      anchor,
      urlUtils.querySettings(),
      true,
    ),
  );
}

/** The anchor-free form, for a recipe opened with `popover.open(id)`. */
export function runPrefetch(attrs: ExperienceAttributes) {
  const expId = attrs.getExperienceId();
  const mapPageUrl = attrs.getUrl();

  if (!expId && !mapPageUrl) {
    return;
  }

  const cacheKey = expId ? String(expId) : `url:${mapPageUrl}`;
  const bucket = _ensurePrefetchedExperiencesBucket();
  const urlKey = mapPageUrl ? `url:${mapPageUrl}` : null;
  const prefetchKeys = [cacheKey, urlKey].filter(Boolean) as string[];

  const playServer = attrs.getPlayServer();
  injectPlayShellPreloadHints(playServer);

  warmDefaultPlayIcons();

  // Video warm (the heavy media fetch) is intentionally NOT started here.
  // This runs on viewport proximity (IntersectionObserver, 200px) for every
  // anchor; warming video for experiences the user only scrolls past is the
  // bulk of media DTO. The video warm is armed on user intent instead — see
  // armWarmOnIntent / kickMediaWarmInBackground (anchorSetup.ts). Here we prefetch only the
  // experience JSON, images, and play-shell.
  const inflight = _getInflightRegistry();
  const reused = _reuseMappedPrefetchEntry(bucket, expId, mapPageUrl);
  if (reused) {
    return;
  }
  const cached = bucket[cacheKey];
  if (cached && cached.status === "ok") {
    return;
  }
  if (_prefetchInflightAny(inflight, prefetchKeys)) {
    return;
  }

  const apiUrl = expId
    ? buildPublisherExperienceApiUrl(attrs)
    : buildPublisherExperienceByUrlApiUrl(attrs);
  if (!apiUrl) {
    return;
  }

  inflight[cacheKey] = true;
  if (urlKey && urlKey !== cacheKey) {
    inflight[urlKey] = true;
  }
  fetch(apiUrl, {
    method: "GET",
    credentials: "omit",
    mode: "cors",
  })
    .then(res =>
      res.ok
        ? res.json().then(
            json => ({ ok: true, json: json }),
            () => ({ ok: false, json: null }),
          )
        : { ok: false, json: null },
    )
    .then(result => {
      delete inflight[cacheKey];
      if (urlKey && urlKey !== cacheKey) {
        delete inflight[urlKey];
      }
      if (!result.ok) {
        bucket[cacheKey] = {
          status: "error",
          fetchedAt: Date.now(),
        };
        return;
      }
      const entry: PrefetchEntry = {
        status: "ok",
        data: result.json,
        fetchedAt: Date.now(),
      };
      bucket[cacheKey] = entry;
      const rid = experienceIdFromPublisherPayload(result.json);
      if (rid) {
        bucket[rid] = entry;
      }
      _notifyOpenOverlayOfPrefetchResolution();
    })
    .catch(() => {
      delete inflight[cacheKey];
      if (urlKey && urlKey !== cacheKey) {
        delete inflight[urlKey];
      }
      bucket[cacheKey] = {
        status: "error",
        fetchedAt: Date.now(),
      };
    });
}

/**
 * Late-arrival: when overlay iframe was opened while prefetch was in flight
 * (URL carried `pix-embed-prefetch=1`), the child is still waiting for the
 * parent payload via postMessage. Push it as soon as the fetch resolves so
 * the child can short-circuit before its 20s timeout falls back to its own API request.
 *
 * Called only on the success path. On prefetch failure, `postPrefetchToIframeWindow`
 * would early-return on the non-ok bucket entry anyway; the child times out and
 * falls back to its own fetch. A future iteration could post an
 * "experience_prefetch_unavailable" signal so the child aborts the wait early.
 */
function _notifyOpenOverlayOfPrefetchResolution() {
  const pop = (window.__pixmoto || {}).popover;
  if (pop && typeof pop._postPrefetchToIframeWindowExternal === "function") {
    pop._postPrefetchToIframeWindowExternal();
  }
}

/**
 * Warm publisher/experience JSON for anchors on host page (CORS or proxy required).
 * No separate prefetch.js — included when sites load only popover.js.
 */
export function setupEmbedExperiencePrefetch() {
  let anchors = document.getElementsByClassName("pixmoto-popover-anchor");
  if (anchors.length === 0) {
    anchors = document.getElementsByClassName("pixmoto_popover_anchor");
  }

  if (!window.IntersectionObserver) {
    for (let i = 0; i < anchors.length; i++) {
      runPrefetchForAnchor(anchors[i]);
    }
    return;
  }

  const io = new IntersectionObserver(
    entries => {
      for (let j = 0; j < entries.length; j++) {
        if (entries[j].isIntersecting) {
          runPrefetchForAnchor(entries[j].target);
          io.unobserve(entries[j].target);
        }
      }
    },
    { root: null, rootMargin: "200px", threshold: 0 },
  );

  for (let k = 0; k < anchors.length; k++) {
    io.observe(anchors[k]);
  }
}
